import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/lib/supabase';
import { KeyRound, CheckCircle2, AlertCircle } from 'lucide-react';

const resetSchema = z
  .object({
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type ResetFormData = z.infer<typeof resetSchema>;

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
  });

  const onSubmit = async (data: ResetFormData) => {
    try {
      setIsSubmitting(true);
      setApiError(null);
      const { error } = await supabase.auth.updateUser({
        password: data.password,
      });

      if (error) {
        setApiError(error.message);
      } else {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err: unknown) {
      setApiError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl border border-brand-border shadow-lg p-6 sm:p-8">
        <h1 className="text-xl font-bold text-brand-black">Set New Password</h1>
        <p className="text-xs text-brand-muted mt-1 mb-6">
          Enter your new password below.
        </p>

        {success ? (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-md text-center text-xs text-brand-green">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-brand-green" />
            <p className="font-semibold">Password Updated!</p>
            <p className="mt-1 text-brand-muted">
              Redirecting you to login in a few seconds...
            </p>
            <Link
              to="/login"
              className="mt-3 inline-block font-semibold text-brand-green underline"
            >
              Go to Login now
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {apiError && (
              <div className="p-3 bg-brand-red-light border border-brand-red/30 rounded-md text-brand-red text-xs flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{apiError}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
                New Password
              </label>
              <input
                type="password"
                placeholder="••••••••"
                {...register('password')}
                className="w-full px-3 py-2.5 bg-brand-bg border border-brand-border rounded-md text-sm text-brand-black"
              />
              {errors.password && (
                <p className="mt-1 text-xs text-brand-red">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                placeholder="••••••••"
                {...register('confirmPassword')}
                className="w-full px-3 py-2.5 bg-brand-bg border border-brand-border rounded-md text-sm text-brand-black"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-xs text-brand-red">{errors.confirmPassword.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full inline-flex items-center justify-center space-x-2 py-2.5 px-4 bg-brand-green text-white font-semibold text-sm rounded-md hover:bg-brand-green-hover transition-colors disabled:opacity-50"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <KeyRound className="w-4 h-4" />
                  <span>Update Password</span>
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
